import {
  Box,
  chakra,
  Divider,
  Heading,
  Img,
  ListItem,
  OrderedList,
  type StyleProps,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import 'katex/dist/katex.min.css'
import type { CSSProperties } from 'react'
import { BlockMath, InlineMath } from 'react-katex'
import { Transforms } from 'slate'
import {
  ReactEditor,
  type RenderElementProps,
  useFocused,
  useReadOnly,
  useSelected,
  useSlate,
} from 'slate-react'

import { getUserUrl, WEBSITE_URL } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import Time from '@/components/UI/Time/Time'

export default function RichTextElement({
  attributes,
  children,
  element,
  styleProps,
}: RenderElementProps & {
  styleProps?: StyleProps
}): React.ReactElement {
  const selected = useSelected()
  const readOnly = useReadOnly()
  const focused = useFocused()
  const editor = useSlate()

  const style: CSSProperties = {
    textAlign: element.align,
  }

  switch (element.type) {
    case 'bulleted-list': {
      return (
        <UnorderedList style={style} {...styleProps} {...attributes} pl="8px">
          {children}
        </UnorderedList>
      )
    }
    case 'numbered-list': {
      return (
        <OrderedList style={style} {...styleProps} {...attributes} pl="8px">
          {children}
        </OrderedList>
      )
    }
    case 'link': {
      return (
        <ShwLink
          cursor={readOnly ? 'pointer' : 'text'}
          href={
            element.url.startsWith('mailto') || element.url.startsWith('http')
              ? element.url
              : `https://${element.url}`
          }
          {...styleProps}
          {...attributes}
          bg={selected ? 'bg.info' : undefined}
          isExternal={!(element.isInternal === true && element.url.startsWith(`${WEBSITE_URL}/`))}
        >
          {children}
        </ShwLink>
      )
    }
    case 'horizontal-line': {
      return <Divider borderTopWidth="2px" borderColor="border.muted" my="12px" />
    }
    case 'blockquote': {
      return (
        <Box
          as="blockquote"
          style={style}
          {...styleProps}
          {...attributes}
          borderLeft="1px solid"
          marginLeft="12px"
          pl="16px"
        >
          {children}
        </Box>
      )
    }
    case 'mention': {
      return (
        <ShwLink
          cursor={readOnly ? 'pointer' : 'default'}
          href={getUserUrl({
            userId: element.userId,
            username: element.username ?? null,
          })}
          isExternal
        >
          <Box
            as="span"
            style={style}
            bg={selected ? 'bg.info' : undefined}
            borderRadius="4px"
            contentEditable={false}
            {...styleProps}
            {...attributes}
            ml="0.2em"
            mr="0.2em"
          >
            <Text
              as="span"
              color={styleProps?.color ?? 'blue.fg'}
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              @{element.displayName}
            </Text>
            {children}
          </Box>
        </ShwLink>
      )
    }
    case 'math': {
      return (
        <Box {...styleProps} {...attributes} as="span">
          <Box
            as={element.displayMode ? 'div' : 'span'}
            style={style}
            bg={selected ? 'yellow.subtle' : undefined}
            contentEditable={false}
          >
            {element.displayMode ? (
              <BlockMath math={element.latex} />
            ) : (
              <InlineMath math={element.latex} />
            )}
          </Box>
          <Box display="none">{children}</Box>
        </Box>
      )
    }
    case 'image': {
      return (
        <Box as="span" ml="0.1em" style={style} contentEditable={false}>
          <Img
            src={element.url}
            {...styleProps}
            {...attributes}
            cursor="default"
            display="inline"
            draggable={false}
            width={element.width == null ? 'auto' : `${element.width}px`}
            minWidth="30px"
            maxWidth="100%"
            border={selected && focused ? '4px solid' : undefined}
            borderColor="yellow.border"
          />
          {children}
        </Box>
      )
    }
    case 'heading': {
      return (
        <Heading size="2xl" style={style} {...styleProps} {...attributes} mb="24px" mt="8px">
          {children}
        </Heading>
      )
    }
    case 'heading-two': {
      return (
        <Heading size="xl" style={style} {...styleProps} {...attributes} mb="24px" mt="8px">
          {children}
        </Heading>
      )
    }
    case 'heading-three': {
      return (
        <Heading size="lg" style={style} {...styleProps} {...attributes} mb="8px" mt="8px">
          {children}
        </Heading>
      )
    }
    case 'list-item': {
      return (
        <ListItem style={style} {...styleProps} {...attributes}>
          {children}
        </ListItem>
      )
    }
    case 'template': {
      return (
        <Text
          style={style}
          {...styleProps}
          {...attributes}
          wordBreak="break-word"
          contentEditable={false}
        >
          {readOnly && element.readOnlyText != null ? (
            <chakra.span fontWeight="bold">{element.readOnlyText}</chakra.span>
          ) : (
            children
          )}
        </Text>
      )
    }
    case 'sup': {
      return (
        <chakra.sup style={style} {...styleProps} {...attributes}>
          {children}
        </chakra.sup>
      )
    }
    case 'date-time': {
      // TODO: Add support for styleProps
      return element.isRelative === true ? (
        <RelativeTime time={element.dateTimeISO} />
      ) : (
        <Time time={element.dateTimeISO} format={element.format} />
      )
    }
    case 'paragraph':
    default: {
      return (
        <Box style={style} {...styleProps} {...attributes} wordBreak="break-word">
          {element.placeholder != null &&
          !readOnly &&
          children.length === 1 &&
          children[0].props.text.text === '' ? (
            <span
              style={{
                opacity: 0.3,
                position: 'absolute',
              }}
              contentEditable={false}
              onClick={() => {
                Transforms.select(editor, ReactEditor.findPath(editor, element))
              }}
            >
              {element.placeholder}
            </span>
          ) : null}
          {children}
        </Box>
      )
    }
  }
}
